// minimal HTML sanitizer for the rich-text agenda fields. content is authored by authenticated
// staff and can be shared with colleagues, so we whitelist a small formatting tag set and drop
// everything else (scripts and styles with what's in them, event handlers, unknown tags → unwrapped to
// their text). The chat has its own stricter rules (packages/shared/src/domain/chat.ts).

const ALLOWED = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV', 'SPAN', 'A',
]);
// elements whose content is never text to keep (code, styling, embedded documents): dropped whole
const DROP = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TITLE', 'TEXTAREA', 'SELECT', 'HEAD']);

/** markup parsed into a document of its own, with no window: nothing in it loads or runs while it is
 *  looked at (in the page's own document an <img onerror> would fire as soon as it was parsed). */
function inertDiv(html: string): HTMLDivElement {
  const div = document.implementation.createHTMLDocument('').createElement('div');
  div.innerHTML = html;
  return div;
}

export function sanitizeHtml(html: string): string {
  if (typeof document === 'undefined' || !html) return html || '';
  const src = inertDiv(html);
  const out = document.createElement('div');
  const clean = (node: Node, dest: HTMLElement) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        dest.appendChild(document.createTextNode(child.nodeValue || ''));
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (DROP.has(el.tagName.toUpperCase())) return;
        if (ALLOWED.has(el.tagName)) {
          const copy = document.createElement(el.tagName.toLowerCase());
          if (el.tagName === 'A') {
            const href = el.getAttribute('href') || '';
            if (/^(https?:|mailto:)/i.test(href)) {
              copy.setAttribute('href', href);
              copy.setAttribute('target', '_blank');
              copy.setAttribute('rel', 'noopener noreferrer');
            }
          }
          clean(el, copy);
          dest.appendChild(copy);
        } else {
          clean(el, dest); // unwrap unknown tags, keep their content
        }
      }
      // comments and everything else are dropped
    });
  };
  clean(src, out);
  return out.innerHTML;
}

/** Plain-text view of stored HTML (for previews / log snippets). */
export function htmlToText(html: string): string {
  if (typeof document === 'undefined') return html;
  const tmp = inertDiv(html);
  tmp.querySelectorAll('script,style,template,noscript,title').forEach((el) => el.remove());
  // textContent runs block elements together ('TodayChase…'); a space after each block or line break
  // keeps the words apart for previews and search (repeats collapse below)
  tmp.querySelectorAll('p,div,li,br,h1,h2,h3,h4,h5,h6,tr').forEach((el) => el.after(' '));
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
}

/** cheap check whether a stored string carries HTML markup (legacy entries are plain text). */
export const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s);

/** Normalise pasted HTML (Word / Google Docs / web) into the sanitized subset the editor keeps:
 *  inline-styled bold/italic/underline become real tags, headings become bold paragraphs, then the
 *  whole thing is run through the sanitizer. Lets rich copy/paste preserve formatting instead of
 *  arriving as flat text (or messy styled spans the sanitizer would drop). */
export function normalizePastedHtml(html: string): string {
  if (typeof document === 'undefined' || !html) return '';
  return sanitizeHtml(pastedStylesToTags(html));
}

/** pasted markup with its inline styling made into tags (bold, italic, underline, strikethrough), its
 *  headings into bold paragraphs and its tables into lines, NOT yet cleaned: the agenda cleans it with
 *  sanitizeHtml (above), the chat with its own stricter rules. */
export function pastedStylesToTags(html: string): string {
  if (typeof document === 'undefined' || !html) return '';
  const src = inertDiv(html);
  // a bold or italic tag styled back to normal is no formatting at all (Google Docs wraps a whole copy in
  // <b style="font-weight:normal">, which would otherwise make everything bold)
  for (const el of Array.from(src.querySelectorAll<HTMLElement>('b[style],strong[style],i[style],em[style]')).reverse()) {
    const s = (el.getAttribute('style') || '').toLowerCase();
    const off = /^(B|STRONG)$/.test(el.tagName) ? /font-weight\s*:\s*(normal|[1-4]00|lighter)/.test(s) : /font-style\s*:\s*normal/.test(s);
    if (off) el.replaceWith(...Array.from(el.childNodes));
  }
  // process deepest-first so wrapping a child isn't clobbered when its parent is later rewritten.
  const styled = Array.from(src.querySelectorAll<HTMLElement>('[style]')).reverse();
  for (const el of styled) {
    const s = (el.getAttribute('style') || '').toLowerCase();
    let inner = el.innerHTML;
    if (/font-weight\s*:\s*(bold|[6-9]00)/.test(s)) inner = `<b>${inner}</b>`;
    if (/font-style\s*:\s*italic/.test(s)) inner = `<i>${inner}</i>`;
    if (/text-decoration[^;]*underline/.test(s)) inner = `<u>${inner}</u>`;
    if (/text-decoration[^;]*line-through/.test(s)) inner = `<s>${inner}</s>`;
    el.innerHTML = inner;
    el.removeAttribute('style');
  }
  for (const h of Array.from(src.querySelectorAll('h1,h2,h3,h4,h5,h6'))) {
    const p = src.ownerDocument.createElement('p');
    p.innerHTML = `<b>${(h as HTMLElement).innerHTML}</b>`;
    h.replaceWith(p);
  }
  tablesToLines(src);
  return src.innerHTML;
}

/** a table (cells copied from Excel, Google Sheets, Smartsheet or a page) as a line per row, its cells set
 *  apart by " | ": neither the agenda nor the chat keeps tables, and cells unwrapped to their text would run
 *  together ("BoatDueTH 4212/10"). Empty rows go, and so do the empty cells at the end of a row (a copied
 *  range is often wider than its text); a cell keeps its formatting. Inner tables go first. */
function tablesToLines(root: HTMLElement): void {
  const doc = root.ownerDocument;
  const isEmpty = (el: Element) => !(el.textContent ?? '').trim(); // (trim takes &nbsp; too)
  for (const table of Array.from(root.querySelectorAll('table')).reverse()) {
    const lines: HTMLElement[] = [];
    const line = (html: string) => {
      const div = doc.createElement('div');
      div.innerHTML = html;
      lines.push(div);
    };
    if (table.caption && !isEmpty(table.caption)) line(table.caption.innerHTML.trim());
    for (const row of Array.from(table.rows)) {
      const cells = Array.from(row.cells);
      while (cells.length && isEmpty(cells[cells.length - 1]!)) cells.pop();
      if (cells.length) line(cells.map((c) => c.innerHTML.trim()).join(' | '));
    }
    table.replaceWith(...lines);
  }
}
